import React, { Component } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import { replace, push } from "react-router-redux";
import { func, number, shape, arrayOf, string as stringProp } from "prop-types";

import { Box, Button as ButtonUi } from "@ui-kit/components/index.es";

import { selectors as sessionSelectors } from "reducers/session";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { selectors as loginSelectors } from "reducers/login";
import { selectors as settingsSelectors } from "reducers/settings";
import { actions as statusActions } from "reducers/status";

import DetailHeadInfo from "pages/accounts/_components/DetailHeadInfo";
import DetailHeadTitle from "pages/accounts/_components/DetailHeadTitle";

import MoreFilters from "pages/accounts/_components/MoreFilters";
import Button from "pages/_components/Button";
import ContextMenu from "pages/_components/ContextMenu";
import Notification from "pages/_components/Notification";
import PageLoading from "pages/_components/PageLoading";
import ProductDetail from "pages/_components/ProductDetail";
import { resizableRoute } from "pages/_components/Resizable";

import { actions as layoutActions } from "reducers/layout";

import TabContent, { TabComponent } from "pages/_components/TabComponent";

import * as dateUtils from "util/date";
import * as i18nUtils from "util/i18n";
import * as configUtil from "util/config";

import { bool, string } from "yup";
import { accountContextMenuItems, getRutPathAccount, maskedAccountNumber, productTypes } from "util/accounts";
import { dispatchV3ShellLayout, isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";

class Details extends Component {
    static propTypes = {
        account: shape({}).isRequired,
        activeEnvironmentForms: shape({
            reduce: func,
        }).isRequired,
        channels: arrayOf().isRequired,
        fetching: bool.isRequired,
        fetchingDownload: func.isRequired,
        fetchingMovements: func.isRequired,
        totalAccounts: number.isRequired,
        match: shape().isRequired,
        isDesktop: bool.isRequired,
        hideAmount: bool.isRequired,
        dispatch: func.isRequired,
        totalCount: number.isRequired,
        activeRegion: string.isRequired,
        loggedUser: shape({}).isRequired,
        accountsHref: string.isRequired,
        embeddedInV3Shell: bool,
        activeEnvironment: shape({}),
        location: shape({
            pathname: stringProp,
        }),
    };

    static defaultProps = {
        embeddedInV3Shell: false,
        activeEnvironment: null,
        location: undefined,
    };

    state = {
        lastMonthPeriod: null,
        secondLastMonthPeriod: null,
        selectedFilter: null,
        tabSelected: 1,
        showPartialAmounts: false,
    };

    componentDidMount() {
        const { dispatch, loggedUser, ...rest } = this.props;
        const initialTab = rest.location && rest.location.state && rest.location.state.tabSelected;

        dispatch(accountsActions.details(rest.match.params.id, loggedUser.userId));

        this.setState((prevState) => ({
            ...prevState,
            lastMonthPeriod: dateUtils.getLastMonthPeriod(),
            secondLastMonthPeriod: dateUtils.getSecondLastMonthPeriod(),
            ...(initialTab ? { tabSelected: initialTab } : {}),
        }));
        dispatch(statusActions.saveLastHref(rest.location));
        this.applyV3ShellAccountHeader();
    }

    componentDidUpdate(prevProps, prevState) {
        const { account, isDesktop, accountsHref, embeddedInV3Shell, fetching, match, hideAmount } = this.props;
        const { showPartialAmounts } = this.state;
        const depsChanged =
            prevProps.account !== account ||
            prevProps.isDesktop !== isDesktop ||
            prevProps.accountsHref !== accountsHref ||
            prevProps.embeddedInV3Shell !== embeddedInV3Shell ||
            prevProps.fetching !== fetching ||
            prevProps.match?.params?.id !== match?.params?.id ||
            prevProps.hideAmount !== hideAmount;
        const partialChanged = prevState.showPartialAmounts !== showPartialAmounts;
        if (depsChanged || partialChanged) {
            this.applyV3ShellAccountHeader();
        }
    }

    componentWillUnmount() {
        const { dispatch, embeddedInV3Shell } = this.props;
        if (embeddedInV3Shell && isUxDashboardImprovementsEnabled()) {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        }
    }

    handleClick = () => {
        const { dispatch } = this.props;

        dispatch(accountsActions.toggleOptions());
    };

    handleFilterButtonsClick = (id) => {
        const { lastMonthPeriod, secondLastMonthPeriod } = this.state;
        const { dispatch, ...rest } = this.props;

        this.setState((prevState) => ({ ...prevState, selectedFilter: id }));

        let period = [];
        switch (id) {
            case "lastMonth":
                period = lastMonthPeriod;
                break;
            case "secondLastMonth":
                period = secondLastMonthPeriod;
                break;
            case "all":
            default:
                break;
        }

        const filters = { dateFrom: period[0], dateTo: period[1] };
        dispatch(accountsActions.fetchMovements(rest.match.params.id, filters));
    };

    handleClickDownloadMovements = (format) => {
        const { dispatch, match } = this.props;
        dispatch(accountsActions.downloadMovements(match.params.id, format));
    };

    formatDocument = (document, documentType) => {
        let documentFormated = "";
        const documentWithoutBlank = document.replace(" ", "").trim();
        if (documentType !== "DNI" && documentWithoutBlank.length > 2) {
            if (documentWithoutBlank.length > 2) {
                documentFormated = documentWithoutBlank.substring(0, 2).concat("-");
            }
            if (documentWithoutBlank.length <= 10) {
                documentFormated += documentWithoutBlank.substring(2);
            }
            if (documentWithoutBlank.length > 10) {
                documentFormated += documentWithoutBlank
                    .substring(2, 10)
                    .concat("-")
                    .concat(documentWithoutBlank.substring(10));
            }
        } else {
            documentFormated = documentWithoutBlank;
        }

        return documentFormated;
    };

    handleClickDownloadInfo = (format) => {
        const { dispatch, match, account } = this.props;
        const { productType, number: accountNumber, cbu, cbuAlias, ownerName, documentNumber, currency } = account;
        const accountType = i18nUtils.get(`accounts.productType.${productTypes[productType]}`).toUpperCase();
        const currencyType = i18nUtils.get(`currency.label.${currency}`);
        const accountLabel = `${accountType} en ${currencyType}`;
        const cuit = documentNumber ? this.formatDocument(documentNumber.toString(), "CUIT") : " - ";

        dispatch(
            accountsActions.downloadInformation(match.params.id, {
                accountLabel,
                accountNumber,
                cbu,
                cbuAlias,
                ownerName,
                cuit,
                format,
            }),
        );
    };

    handleBack = () => {
        const { dispatch } = this.props;
        dispatch(replace(this.resolveAccountsBackHref()));
    };

    handleTab = (value) => {
        this.setState((prevState) => ({ ...prevState, tabSelected: value }));
    };

    showFilters = (totalCount) => totalCount > configUtil.get("accounts.statementsPerPage");

    resolveAccountsBackHref() {
        const { accountsHref, embeddedInV3Shell } = this.props;
        const uxDash = isUxDashboardImprovementsEnabled();
        if (embeddedInV3Shell && uxDash && accountsHref === "/desktop") {
            return getRutPathAccount();
        }
        return accountsHref;
    }

    accountDetailMobileShellRightElement() {
        const { account, dispatch, activeEnvironment, hideAmount, embeddedInV3Shell, location } = this.props;
        const { showPartialAmounts } = this.state;
        if (!embeddedInV3Shell || !account || !activeEnvironment) {
            return null;
        }
        const { idProduct } = account;
        const accountNumber = account.number;
        const enableHideAmounts = configUtil.getBoolean("frontend.hide.accounts.balance.funcionalities", false);
        const arrayItemsDropdown = [
            {
                label: "products.alias.account.edit.title.mobile",
                onClick: () => dispatch(push(`/account/${idProduct}/setProductAlias`)),
                icon: "Pencil",
            },
            {
                label: "accounts.projected.balance",
                onClick: () => {
                    dispatch(push(`/account/proyectedBalance/${idProduct}`));
                    if (accountNumber) {
                        dispatch(accountsActions.proyectedBalance(idProduct));
                        dispatch(accountsActions.diferedMovements(idProduct, "1"));
                    }
                },
                icon: "StocksUp",
            },
            {
                label: "accounts.historic.balance",
                onClick: () => dispatch(push(`/account/historicBalance/${idProduct}`)),
                icon: "MemoMoney",
            },
        ];
        return (
            <Box flex align="center" gap={12}>
                {enableHideAmounts && hideAmount && (
                    <ButtonUi
                        variant="text"
                        type="button"
                        aria-label="Mostrar saldos durante 3 segundos"
                        icon={!showPartialAmounts ? "EyeOff" : "Eye"}
                        iconVariant="outline"
                        color="white"
                        size="large"
                        css={{ padding: 8 }}
                        onClick={() => {
                            this.setState({ showPartialAmounts: true });
                            window.setTimeout(() => {
                                this.setState({ showPartialAmounts: false });
                            }, 3000);
                        }}
                    />
                )}
                <ContextMenu
                    isDesktop={false}
                    color="white"
                    items={accountContextMenuItems(
                        dispatch,
                        activeEnvironment,
                        idProduct,
                        arrayItemsDropdown,
                        location?.pathname,
                    )}
                />
            </Box>
        );
    }

    applyV3ShellAccountHeader() {
        const { embeddedInV3Shell, isDesktop, account, dispatch, fetching } = this.props;
        if (!embeddedInV3Shell || !isUxDashboardImprovementsEnabled()) {
            return;
        }
        if (fetching || !account) {
            return;
        }

        if (!isDesktop) {
            const title = account.productAlias || maskedAccountNumber(account.number);
            const accountTypeFormatted = i18nUtils.get(`accounts.productType.${productTypes[account.productType]}`);
            const headerSubtitle = `${accountTypeFormatted.toUpperCase()} Nº ${maskedAccountNumber(account.number)}`;
            dispatchV3ShellLayout(dispatch, {
                headerTitle: title,
                headerSubtitle,
                goBackPath: this.resolveAccountsBackHref(),
                scopes: ["account/details", "account/alias"],
                rightContentMobile: this.accountDetailMobileShellRightElement(),
            });
        } else {
            dispatch(layoutActions.setScopes(["account/details", "account/alias"]));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        }
    }

    render() {
        const { secondLastMonthPeriod, selectedFilter, tabSelected } = this.state;
        const {
            totalCount,
            account,
            channels,
            fetching,
            fetchingDownload,
            fetchingMovements,
            dispatch,
            isDesktop,
            activeRegion,
            embeddedInV3Shell,
            ...rest
        } = this.props;
        const { showPartialAmounts } = this.state;

        const filters = [
            <Button
                className={classNames("btn btn-outline", {
                    "is-active": selectedFilter === "all",
                })}
                key="all"
                label="accounts.movements.filters.all"
                onClick={() => this.handleFilterButtonsClick("all")}
            />,
            <Button
                className={classNames("btn btn-outline", {
                    "is-active": selectedFilter === "lastMonth",
                })}
                key="lastMonth"
                label="accounts.movements.filters.lastMonth"
                onClick={() => this.handleFilterButtonsClick("lastMonth")}
            />,
            <Button
                className={classNames("btn btn-outline", {
                    "is-active": selectedFilter === "secondLastMonth",
                })}
                key="secondLastMonth"
                label="accounts.movements.filters.secondLastMonth"
                onClick={() => this.handleFilterButtonsClick("secondLastMonth")}
                replace={{
                    SECOND_LAST_MONTH:
                        secondLastMonthPeriod &&
                        `${
                            i18nUtils.getArray("global.months")[secondLastMonthPeriod[0].getMonth()]
                        } ${secondLastMonthPeriod[0].getFullYear().toString()}`,
                }}
            />,
        ];

        return (
            <PageLoading loading={fetching} className="screen-loader">
                {!fetching && account && (
                    <>
                        {!isUxDashboardImprovementsEnabled() && (
                            <Notification
                                scopeToShow="account/details"
                                notificationClassname={!isDesktop ? "z-index-9" : ""}
                            />
                        )}
                        <ProductDetail>
                            <ProductDetail.Head
                                onBack={embeddedInV3Shell && !isDesktop ? null : this.handleBack}
                                dispatch={dispatch}
                                hideDownload
                                productId={rest.match.params.id}
                                title="account.detail.title"
                                embeddedInV3Shell={embeddedInV3Shell}>
                                {isDesktop && <div />}
                                <DetailHeadTitle
                                    account={account}
                                    isDesktop={isDesktop}
                                    dispatch={dispatch}
                                    showPartialAmounts={showPartialAmounts}
                                    setShowPartialAmounts={(show) => this.setState({ showPartialAmounts: show })}
                                    embeddedInV3Shell={embeddedInV3Shell}
                                    contextMenuEntryPathname={rest.location?.pathname}
                                />
                                <DetailHeadInfo {...this.props} showPartialAmounts={showPartialAmounts}>
                                    <div className="tab-container">
                                        <TabComponent
                                            tabSelected={tabSelected}
                                            changeState={this.handleTab}
                                            isDesktop={isDesktop}
                                        />
                                    </div>
                                </DetailHeadInfo>
                            </ProductDetail.Head>

                            <div className="accounts-main-content">
                                <ProductDetail.Body
                                    closeMoreFiltersWhenSiblingClick
                                    isDesktop={isDesktop}
                                    filters={this.showFilters(totalCount) ? filters : null}
                                    filtersKeyLabel="accounts.movements"
                                    moreFilters={
                                        <MoreFilters
                                            channels={channels}
                                            currency={account.currency}
                                            dispatch={dispatch}
                                            isDesktop={isDesktop}
                                            isSubmitting={rest.isSubmitting}
                                            productId={rest.match.params.id}
                                        />
                                    }
                                    moreFiltersClosedKeyLabel="accounts.movements.filters.more"
                                    moreFiltersOpenedKeyLabel="accounts.movements.filters.less">
                                    <div>
                                        <TabContent
                                            account={account}
                                            isDesktop={isDesktop}
                                            tabSelected={tabSelected}
                                            onClickDownloadInfo={this.handleClickDownloadInfo}
                                            showPartialAmounts={showPartialAmounts}
                                            {...this.props}
                                        />
                                    </div>
                                </ProductDetail.Body>
                            </div>
                        </ProductDetail>
                    </>
                )}
            </PageLoading>
        );
    }
}

const mapStateToProps = (state) => ({
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    account: accountsSelectors.getSelectedAccount(state),
    totalAccounts: accountsSelectors.getTotalAccounts(state),
    channels: configUtil.getArray("core.enabledChannels"),
    fetching: accountsSelectors.getFetching(state),
    fetchingDownload: accountsSelectors.getFetchingDownload(state),
    fetchingMovements: accountsSelectors.getFetchingMovements(state),
    activeEnvironmentForms: sessionSelectors.getActiveEnvironmentForms(state, "accounts"),
    totalCount: accountsSelectors.getTotalCount(state),
    activeRegion: loginSelectors.getRegion(state),
    loggedUser: sessionSelectors.getUser(state),
    credentialGroups: accountsSelectors.getCredentialGroups(state),
    accountsHref: accountsSelectors.getAccountsLastHref(state),
    hideAmount: settingsSelectors.getUserData(state)?.hideBalance,
});

export default resizableRoute(connect(mapStateToProps)(Details));
